import { useCallback, useEffect, useState } from 'react'
import { useApi } from '../services/api'
import { useAccessToken, useIdTokenClaims } from '../auth/authContexts'
import { isAccessTokenMfaSatisfied, isMfaSatisfiedFromTokenClaims } from '../utils/portalMfa'

export type PortalMfaGateState = {
  loading: boolean
  hasLinkedPolicies: boolean
  portalMfaEnforced: boolean
  mfaSatisfied: boolean
  mfaRequiredForDetail: boolean
  smsMfaEnrolled: boolean
  smsMfaMaskedPhone: string | null
  mfaFactorEnrolled: boolean
  refresh: () => Promise<void>
}

/**
 * Portal MFA gate for policy detail / claims routes (dashboard list stays available).
 */
export function usePortalMfaGate(): PortalMfaGateState {
  const api = useApi()
  const getAccessToken = useAccessToken()
  const getIdTokenClaims = useIdTokenClaims()
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE?.trim()
  const [loading, setLoading] = useState(true)
  const [hasLinkedPolicies, setHasLinkedPolicies] = useState(false)
  const [portalMfaEnforced, setPortalMfaEnforced] = useState(false)
  const [mfaSatisfied, setMfaSatisfied] = useState(false)
  const [smsMfaEnrolled, setSmsMfaEnrolled] = useState(false)
  const [smsMfaMaskedPhone, setSmsMfaMaskedPhone] = useState<string | null>(null)
  const [mfaFactorEnrolled, setMfaFactorEnrolled] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const user = await api.getUser()
      setHasLinkedPolicies(user.hasLinkedPolicies === true)
      setPortalMfaEnforced(user.portalMfaEnforced === true)
      setSmsMfaEnrolled(user.smsMfaEnrolled === true)
      setMfaFactorEnrolled(user.mfaFactorEnrolled === true || user.smsMfaEnrolled === true)
      setSmsMfaMaskedPhone(
        typeof user.smsMfaMaskedPhone === 'string' && user.smsMfaMaskedPhone
          ? user.smsMfaMaskedPhone
          : user.smsMfaPhoneLast4
            ? `•••• ${user.smsMfaPhoneLast4}`
            : null,
      )
      const token = await getAccessToken()
      const idClaims = await getIdTokenClaims()
      const tokenMfa =
        isAccessTokenMfaSatisfied(token) ||
        isMfaSatisfiedFromTokenClaims(idClaims, audience)
      setMfaSatisfied(user.mfaSatisfied === true || tokenMfa)
    } catch {
      setHasLinkedPolicies(false)
      setPortalMfaEnforced(false)
      setMfaSatisfied(false)
      setSmsMfaEnrolled(false)
      setSmsMfaMaskedPhone(null)
      setMfaFactorEnrolled(false)
    } finally {
      setLoading(false)
    }
  }, [api, audience, getAccessToken, getIdTokenClaims])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const mfaRequiredForDetail =
    portalMfaEnforced && hasLinkedPolicies && !mfaSatisfied

  return {
    loading,
    hasLinkedPolicies,
    portalMfaEnforced,
    mfaSatisfied,
    mfaRequiredForDetail,
    smsMfaEnrolled,
    smsMfaMaskedPhone,
    mfaFactorEnrolled,
    refresh,
  }
}
